import { Link } from "react-router-dom";
import { ArrowRight, Target, Heart, Leaf, Users } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../data/content";

const values = [
  [Target, "Right measure, right home", "We never push products. Every recommendation comes from an honest survey of your property."],
  [Heart, "Access for everyone", "By maximising grants and finance, we make efficiency upgrades possible without upfront cost."],
  [Leaf, "Lower carbon, lower bills", "Helping UK homes use less energy is good for households and the planet alike."],
  [Users, "A trusted network", "We partner only with accredited, vetted installers who meet the highest standards."],
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        overline="About us"
        title="On a mission to make every home efficient"
        subtitle="Network Survey Limited connects UK homeowners with the funding and the accredited installers needed to upgrade their homes — affordably."
      />

      <section className="py-16 md:py-24 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="rounded-3xl overflow-hidden">
            <img src={IMAGES.family} alt="Warm home" className="w-full h-[440px] object-cover" />
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-brand mb-5">Energy efficiency, made simple and fair</h2>
            <p className="font-figtree text-lg text-stone-600 mb-4">
              Too many households put off vital home improvements because of the upfront cost — even when grants and finance could cover most or all of it.
            </p>
            <p className="font-figtree text-lg text-stone-600 mb-4">
              We exist to close that gap. We survey your home, identify the most effective measures, and handle the funding paperwork so you can enjoy a warmer, cheaper-to-run home without the financial barrier.
            </p>
            <p className="font-figtree text-lg text-stone-600">
              Behind every installation is our trusted network of MCS, Gas Safe and TrustMark accredited professionals.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-3">What we stand for</p>
            <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-brand mb-12">Our values</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.08}>
                <div className="bg-brand-cream border border-stone-200 rounded-2xl p-7 h-full">
                  <Icon className="w-8 h-8 text-brand-accent mb-4" />
                  <h3 className="font-outfit text-lg font-medium text-brand mb-2">{t}</h3>
                  <p className="font-figtree text-sm text-stone-600">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-brand-cream mb-6">Let's upgrade your home together</h2>
          <Link to="/contact" data-testid="about-cta-button" className="bg-brand-accent text-white hover:bg-brand-accenthover transition-colors rounded-full px-8 py-4 font-medium inline-flex items-center gap-2">
            Book a free survey <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
